/**
 * The whole auth state machine for the capture client, as a pure reducer.
 * Three states an operator can observe: `restoring` (the app is asking the
 * Cognito SDK whether a stored session is still good), `signed-in`, and
 * `signed-out`. Every route guard and every screen reads these, so the
 * transitions are here rather than spread across effects.
 *
 * Session-bearing actions carry `nowMs` so the expiry decision is a
 * function of its inputs and can be tested without faking the clock.
 */

import { isTokenExpired } from './token';
import type { AuthSession, AuthUser } from './types';

export type AuthStatus = 'restoring' | 'signed-in' | 'signed-out';

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  idToken: string | null;
  expiresAt: number | null;
  /** Message for the sign-in screen. Never carries a credential value. */
  error: string | null;
  /** True when the last transition out of `signed-in` was an expiry rather
   * than a deliberate sign-out, so the sign-in screen can say why. */
  expired: boolean;
}

export type AuthAction =
  | { type: 'session-restored'; session: AuthSession; nowMs: number }
  | { type: 'session-absent' }
  | { type: 'signed-in'; session: AuthSession; nowMs: number }
  | { type: 'token-refreshed'; session: AuthSession; nowMs: number }
  | { type: 'sign-in-failed'; message: string }
  | { type: 'session-expired' }
  | { type: 'signed-out' };

export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Sign in again to keep capturing.';

export const initialAuthState: AuthState = {
  status: 'restoring',
  user: null,
  idToken: null,
  expiresAt: null,
  error: null,
  expired: false,
};

function signedOut(error: string | null, expired: boolean): AuthState {
  return { status: 'signed-out', user: null, idToken: null, expiresAt: null, error, expired };
}

export function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'session-restored':
    case 'signed-in':
    case 'token-refreshed': {
      // A restored token can be past its expiry while the SDK still holds
      // a usable refresh token. Landing in `signed-out` here is correct:
      // the provider refreshes first and dispatches `token-refreshed` with
      // the new token, so this branch only fires when refresh is not on.
      if (isTokenExpired(action.session.idToken, action.nowMs)) {
        return signedOut(SESSION_EXPIRED_MESSAGE, true);
      }
      return {
        status: 'signed-in',
        user: action.session.user,
        idToken: action.session.idToken,
        expiresAt: action.session.expiresAt,
        error: null,
        expired: false,
      };
    }
    case 'session-absent':
      return signedOut(null, false);
    case 'sign-in-failed':
      return signedOut(action.message, false);
    case 'session-expired':
      // Only a signed-in session can expire. From `restoring` or
      // `signed-out` there was nothing to lose, so the sign-in screen stays
      // quiet instead of telling a first-time visitor their session expired.
      if (state.status !== 'signed-in') return signedOut(state.error, false);
      return signedOut(SESSION_EXPIRED_MESSAGE, true);
    case 'signed-out':
      return signedOut(null, false);
    default:
      return state;
  }
}
